import { useShallow } from 'zustand/react/shallow';
import { useCallback } from 'react';
import { useReportId } from 'hooks/helperHooks';
import { useReportQueries } from 'utilities/reportQueriesFactory';
import useReportStore from './reportStore';
import { useStores } from 'index';
import { useReportLayoutManagement } from 'hooks/useReportLayoutManagement';
import { DefaultRoutes } from 'Routes';

// This hook connects queries and store with the rest of the app. This is the only hook that should be used to access data and queries.

const usePlayerReportPitcher = () => {
  const codegenReportService = useStores().codegenReportService;
  const { isCreate, reportId } = useReportId();

  const actions = useReportStore((state) => state.actions);

  const data = useReportStore(
    useShallow((state) => ({
      isEditMode: state.isEditMode,
      searchCriteria: state.searchCriteria,
      searchChips: state.searchChips,
      specialConfigOptions: state.specialConfigOptions,
      selectedConfgValues: state.selectedConfgValues,
      layout: state.layout,
      chartData: state.chartData,
      tempLayout: state.tempLayout,
      editingChart: state.editingChart,
    }))
  );

  const { isLoadingReport, saveOrgLayout, generateReportMutation, updateReportSetupMutation } = useReportQueries({
    reportRoute: DefaultRoutes.PlayerReport.Pitcher,
    useReportStore: useReportStore,
    getOrgLayoutFn: codegenReportService.getReportPlayerReportPitcherOrgLayout,
    saveOrgLayoutFn: codegenReportService.postReportPlayerReportPitcherOrgLayout,
    generateReportFn: codegenReportService.postReportPlayerReportPitcherGenerate,
    getReportDataFn: codegenReportService.postReportPlayerReportPitcherGetReportData,
    updateReportSetupFn: codegenReportService.postReportPlayerReportPitcherUpdateReportSetup,
    setReportMetadata: (data) => {
      const { pitchTypeInfo, configData, chartData, ...rest } = data;
      return {
        newSearchChips: {
          seasonNames: rest.seasonNames,
          dateRanges: rest.dateRanges,
          levelName: rest.levelName,
          teamName: rest.teamName,
          playerNames: chartData.map((item) => item.playerName),
        },
        newSpecialConfigOptions: { pitchTypeInfo },
      };
    },
  });

  const { hasUnsavedEdits, discardLayoutChanges, saveLayoutForSession, saveLayoutForOrg } = useReportLayoutManagement({
    isEditMode: data.isEditMode,
    tempLayout: data.tempLayout,
    layout: data.layout,
    setTempLayout: actions.setTempLayout,
    setLayout: actions.setLayout,
    reportId,
    isCreate,
    selectedConfgValues: data.selectedConfgValues,
    updateReportSetupMutation,
    saveOrgLayout,
  });

  const getNewData = useCallback(() => {
    actions.setCanGetNewData(true);
  }, [actions]);

  const generateReport = useCallback(() => {
    actions.setCanGetNewData(false);
    const { gameType, dateRanges, seasonNames, level, team, players } = data.searchCriteria;
    if (data.layout && gameType && dateRanges && level && team && players) {
      generateReportMutation({
        requestBody: {
          gameType,
          dateRanges,
          seasonNames: seasonNames ?? [],
          level,
          team,
          players,
          reportContentConfig: data.selectedConfgValues,
          configData: data.layout,
        },
      });
    }
  }, [data.searchCriteria, data.layout, data.selectedConfgValues, actions, generateReportMutation]);

  const setSearchCriteria = useCallback(
    (searchCriteria: any) => {
      actions.setPartialSearchCriteria(searchCriteria);
      actions.setPartialSearchChips({
        ...('dateRanges' in searchCriteria && { dateRanges: searchCriteria.dateRanges }),
        ...('seasonNames' in searchCriteria && { seasonNames: searchCriteria.seasonNames }),
        ...('level' in searchCriteria && { levelName: searchCriteria.level?.name }),
        ...('team' in searchCriteria && { teamName: searchCriteria.team?.name }),
        ...('players' in searchCriteria && { playerNames: searchCriteria.players?.map((p: any) => p.name) }),
      });
    },
    [actions]
  );

  return {
    ...data,
    ...actions,
    isLoadingReport,
    generateReport,
    getNewData,
    setSearchCriteria,
    hasUnsavedEdits,
    discardLayoutChanges,
    saveLayoutForSession,
    saveLayoutForOrg,
  };
};

export default usePlayerReportPitcher;
